// Single source of truth for backend configuration.
//
// The panel is configured with ONE origin (scheme + host). Everything else is derived:
//   API base   → {origin}/api/v1
//   Media base → {origin}/media
//   Health     → {origin}/healthz, {origin}/readyz
//
// The origin can be overridden at runtime from the Settings page (persisted to localStorage)
// so the panel can be re-pointed at staging/local without a rebuild.

const LS_ORIGIN_KEY = 'cg.apiOrigin';

const stripTrailingSlash = (value) => (value || '').replace(/\/+$/, '');

const DEFAULT_ORIGIN = stripTrailingSlash(
  import.meta.env.VITE_API_ORIGIN || 'https://api.contentgen.dextora.org',
);

/** The origin baked in at build time (the fallback when there is no runtime override). */
export const defaultOrigin = DEFAULT_ORIGIN;

/** Current backend origin — runtime override (localStorage) wins over the build-time value. */
export function getApiOrigin() {
  try {
    const override = localStorage.getItem(LS_ORIGIN_KEY);
    if (override) return stripTrailingSlash(override);
  } catch {
    /* localStorage unavailable (SSR / privacy mode) — fall through to default */
  }
  return DEFAULT_ORIGIN;
}

/** Persist a runtime origin override. Pass an empty value to clear it and fall back to default. */
export function setApiOrigin(origin) {
  const clean = stripTrailingSlash(origin);
  try {
    if (clean) localStorage.setItem(LS_ORIGIN_KEY, clean);
    else localStorage.removeItem(LS_ORIGIN_KEY);
  } catch {
    /* ignore write failures */
  }
}

/** True when a runtime override is currently in effect. */
export function hasOriginOverride() {
  try {
    return Boolean(localStorage.getItem(LS_ORIGIN_KEY));
  } catch {
    return false;
  }
}

/** Full API base, e.g. http://host/api/v1 */
export const getApiBase = () => `${getApiOrigin()}/api/v1`;

/** Media file base, e.g. http://host/media */
export const getMediaBase = () => `${getApiOrigin()}/media`;

const env = {
  appName: import.meta.env.VITE_APP_NAME || 'Content-Gen Panel',
  defaultOrigin: DEFAULT_ORIGIN,
  isDev: import.meta.env.DEV,
  // Chapter upload is disabled for now. Set VITE_UPLOAD_ENABLED=true to bring it back.
  uploadEnabled: String(import.meta.env.VITE_UPLOAD_ENABLED ?? 'false') === 'true',
  // Media generation is disabled for now. Set VITE_GENERATE_ENABLED=true to bring it back.
  generateEnabled: String(import.meta.env.VITE_GENERATE_ENABLED ?? 'false') === 'true',
};

export default env;
